const baseButton = {
  border: "none",
  padding: "13px 22px",
  borderRadius: 14,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  transition: "all 0.25s ease",
  boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
};

const styles = {
  /* ── Card ── */
  card: {
    background: "linear-gradient(160deg, #ffffff 60%, #f0fafa 100%)",
    borderRadius: 22,
    padding: "0 0 18px",
    marginBottom: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    lineHeight: 1.55,
    transition: "box-shadow 0.3s ease, transform 0.2s ease",
    overflow: "hidden",
    position: "relative",
  },

  cardHover: {
    boxShadow: "0 12px 36px rgba(2,136,145,0.18)",
    transform: "translateY(-2px)",
  },

  /* ── Accent bar at top ── */
  accentBar: {
    height: 5,
    background: "linear-gradient(90deg, #028891, #3eadb5, #028891)",
    backgroundSize: "200% 100%",
    animation: "shimmer 3s linear infinite",
    borderRadius: "22px 22px 0 0",
    marginBottom: 0,
  },

  /* ── Inner padding wrapper ── */
  cardInner: {
    padding: "18px 22px 0",
  },

  /* ── Top row: speaker + time ── */
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },

  speaker: {
    fontSize: 13,
    fontWeight: 700,
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "rgba(2,136,145,0.07)",
    padding: "4px 12px",
    borderRadius: 999,
    border: "1px solid rgba(2,136,145,0.12)",
  },

  time: {
    fontSize: 30,
    fontWeight: 900,
    background: "linear-gradient(135deg, #028891, #3eadb5)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    whiteSpace: "nowrap",
    letterSpacing: "-0.5px",
  },

  timeMobile: {
    fontSize: 24,
    fontWeight: 900,
    background: "linear-gradient(135deg, #028891, #3eadb5)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    whiteSpace: "nowrap",
  },

  /* ── Topic ── */
  topic: {
    fontSize: 20,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 10,
    lineHeight: 1.3,
  },

  topicMobile: {
    fontSize: 17,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 10,
    lineHeight: 1.3,
  },

  /* ── Reading passage chip ── */
  readingChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#f0fafa",
    border: "1px solid #b2dfdb",
    borderRadius: 999,
    padding: "4px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "#00796b",
    cursor: "pointer",
    marginBottom: 10,
    transition: "background 0.2s ease, color 0.2s ease, border-color 0.2s ease",
  },

  /* ── Badges row ── */
  badgesRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 10,
    alignItems: "center",
  },

  planBadge: {
    fontSize: 12,
    fontWeight: 700,
    padding: "5px 14px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
    color: "#0369a1",
    border: "1px solid #bae6fd",
  },

  planBadgeOneOnOne: {
    fontSize: 12,
    fontWeight: 700,
    padding: "5px 14px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #fdf4ff, #f3e8ff)",
    color: "#7c3aed",
    border: "1px solid #e9d5ff",
  },

  slotsBadge: {
    fontSize: 12,
    fontWeight: 700,
    padding: "5px 14px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #fef2f2, #fee2e2)",
    color: "#b91c1c",
    border: "1px solid #fecaca",
  },

  /* ── Status badges ── */
  bookedBadge: {
    marginBottom: 10,
    padding: "8px 16px",
    background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    color: "#15803d",
    fontSize: 13,
    fontWeight: 700,
    borderRadius: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #bbf7d0",
  },

  cancelledBadge: {
    marginBottom: 10,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "linear-gradient(135deg, #fef2f2, #fee2e2)",
    color: "#b91c1c",
    padding: "8px 16px",
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    border: "1px solid #fecaca",
  },

  upgradeReason: {
    marginBottom: 10,
    fontSize: 13,
    color: "#b45309",
    fontWeight: 500,
    lineHeight: 1.4,
    padding: "8px 14px",
    background: "linear-gradient(135deg, #fffbeb, #fef3c7)",
    borderRadius: 10,
    border: "1px solid #fde68a",
  },

  /* ── Divider ── */
  divider: {
    height: 1,
    background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
    margin: "14px 0",
  },

  /* ── Action row ── */
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "0 22px",
  },

  actionRowMobile: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "0 16px",
  },

  shareBtn: {
    background: "none",
    border: "1px solid #e2e8f0",
    cursor: "pointer",
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "all 0.2s ease",
    color: "#64748b",
  },

  shareBtnCopied: {
    color: "#16a34a",
    border: "1px solid #bbf7d0",
    background: "#f0fdf4",
  },

  /* ── CTA Buttons ── */
  bookBtn: {
    ...baseButton,
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#ffffff",
    boxShadow: "0 4px 16px rgba(37,99,235,0.35)",
  },

  joinBtn: {
    ...baseButton,
    background: "linear-gradient(135deg, #16a34a, #15803d)",
    color: "#ffffff",
    boxShadow: "0 4px 16px rgba(22,163,74,0.35)",
  },

  cancelBtn: {
    ...baseButton,
    background: "#ffffff",
    color: "#dc2626",
    border: "1.5px solid #fecaca",
    boxShadow: "none",
  },

  upgradeBtn: {
    ...baseButton,
    background: "linear-gradient(135deg, #f59e0b, #d97706)",
    color: "#ffffff",
    boxShadow: "0 4px 16px rgba(245,158,11,0.35)",
  },

  expiredBtn: {
    ...baseButton,
    background: "#f1f5f9",
    color: "#94a3b8",
    cursor: "not-allowed",
    boxShadow: "none",
  },

  bookingClosedBtn: {
    ...baseButton,
    background: "#f1f5f9",
    color: "#94a3b8",
    cursor: "not-allowed",
    boxShadow: "none",
  },

  preparingBtn: {
    ...baseButton,
    background: "linear-gradient(135deg, #fb923c, #f97316)",
    color: "#ffffff",
    cursor: "not-allowed",
    boxShadow: "0 4px 14px rgba(249,115,22,0.3)",
  },

  /* ── Agenda ── */
  agendaToggle: {
    marginTop: 14,
    marginLeft: 22,
    padding: "8px 0",
    border: "none",
    background: "transparent",
    color: "#028891",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  agendaWrap: {
    marginTop: 12,
    marginLeft: 22,
    marginRight: 22,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  agendaCard: {
    background: "linear-gradient(150deg, #f8fafc 0%, #f0fafa 100%)",
    padding: "16px 18px",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid #028891",
  },

  agendaTitle: {
    fontWeight: 800,
    fontSize: 14,
    marginBottom: 12,
    color: "#028891",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  sectionTitle: {
    fontWeight: 700,
    fontSize: 13,
    marginBottom: 6,
    marginTop: 8,
    color: "#334155",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 8px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.2s ease",
  },

  question: {
    fontSize: 13,
    color: "#475569",
    paddingLeft: 14,
    marginBottom: 5,
    lineHeight: 1.6,
  },

  /* ── Alert overlay ── */
  customAlertOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    backdropFilter: "blur(4px)",
  },

  customAlertBox: {
    background: "#fff",
    borderRadius: 20,
    padding: "32px 36px",
    textAlign: "center",
    boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
    maxWidth: 340,
    width: "90%",
  },

  customAlertText: {
    fontSize: 15,
    color: "#0f172a",
    marginBottom: 22,
    fontWeight: 500,
    lineHeight: 1.6,
  },

  customAlertButton: {
    background: "linear-gradient(135deg, #028891, #3eadb5)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "11px 36px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(2,136,145,0.3)",
  },
};

export default styles;
